import { useEffect, useRef, useState } from 'react';
import { CircleCheck, Download, Eye, FileUp, GitCompare, Loader2, MessageSquareText, RotateCcw, Send } from 'lucide-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/format';
import { getErrorMessage } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/ui/status-pill';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { REVIEW_STATUSES, fileExtension, hasFile, isPreviewable, reviewLabel, reviewStatusOf, reviewTrail } from './documentsModel';
import { DOCUMENT_ACCEPT } from './ui';
import DocumentPreview from './DocumentPreview';
import ReviewStatusControl, { ReviewStatusPill } from './ReviewStatusControl';

const linkClass = 'inline-flex items-center gap-1 rounded-[3px] font-semibold text-brand-ink underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

function Sep() {
  return <span className="text-ink-4" aria-hidden="true">·</span>;
}

const TRAIL_ICONS = {
  submitted: Send,
  approved: CircleCheck,
  approved_as_noted: CircleCheck,
  revise_resubmit: RotateCcw,
};

/** Review trail of the current version (16 idea 8): every status logged, oldest first, with who and when. */
function ReviewTrail({ doc, canEdit, onReviewSaved }) {
  const trail = reviewTrail(doc);
  return (
    <div className="min-w-0 sm:col-span-2 lg:col-span-1 lg:col-start-2 xl:col-start-auto" data-testid={`document-review-trail-${doc.id}`}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">
          Review trail · <span className="font-mono normal-case">v{doc.version || 1}</span>
        </p>
      </div>
      {trail.length ? (
        <ol className="space-y-2.5">
          {trail.map((entry, index) => {
            const Icon = TRAIL_ICONS[entry.status] || MessageSquareText;
            const tone = REVIEW_STATUSES[entry.status]?.tone || 'gray';
            return (
              // eslint-disable-next-line react/no-array-index-key
              <li key={`${entry.at}-${index}`} className="flex gap-2.5">
                <span className={cn('ui-icon-tile mt-0.5 h-6 w-6 shrink-0 rounded-md', `tone-${tone === 'outline' ? 'gray' : tone}`)} aria-hidden="true">
                  <Icon className="h-3.5 w-3.5" />
                </span>
                <span className="min-w-0 text-[12.5px] leading-snug">
                  <span className="block font-semibold text-ink">
                    {entry.status === 'submitted' && entry.partyLabel ? `Submitted to ${entry.partyLabel}` : entry.label}
                  </span>
                  <span className="block text-[12px] text-ink-3">
                    {[entry.status !== 'submitted' ? entry.partyLabel : '', formatDate(entry.date), entry.by].filter(Boolean).join(' · ')}
                  </span>
                  {entry.note ? <span className="mt-0.5 block text-[12px] text-ink-2">“{entry.note}”</span> : null}
                </span>
              </li>
            );
          })}
        </ol>
      ) : (
        <p className="text-[12.5px] leading-snug text-ink-3">
          No submission logged for this version yet{REVIEW_STATUSES[reviewStatusOf(doc)] ? ` — it counts as ${reviewLabel(reviewStatusOf(doc))}` : ''}.
        </p>
      )}
      {canEdit ? (
        <div className="mt-2.5">
          <ReviewStatusControl doc={doc} canEdit onSaved={onReviewSaved} trigger="button" />
        </div>
      ) : null}
    </div>
  );
}

/**
 * Version history of one document, opened from its row (16 idea 3): every version newest first with
 * date, uploader and what changed; older ones are "Superseded" and can be opened or compared with the
 * current one. On the right: Open / Download and "Drop a file to save as v3" (16 idea 2).
 */
export default function DocumentVersionsPanel({
  doc, versions, canEdit, onOpen, onDownload, onAttach, onUploadVersion, onCompare, onReviewSaved,
}) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);
  const latest = versions[0] || doc;
  const nextVersion = (Number(latest.version) || 1) + 1;
  const canCompareWith = (older) => older.id !== latest.id && isPreviewable(older) && isPreviewable(latest);

  const takeFile = (file) => { if (file) onUploadVersion(latest, file); };

  return (
    <div
      className="grid gap-5 sm:grid-cols-[176px_minmax(0,1fr)] lg:grid-cols-[176px_minmax(0,1fr)_220px] lg:gap-6 xl:grid-cols-[176px_minmax(0,1fr)_minmax(180px,230px)_200px]"
      data-testid={`document-versions-${doc.id}`}
    >
      <div className="min-w-0">
        <DocumentPreview doc={latest} canEdit={canEdit} onOpen={onOpen} onDownload={onDownload} onAttach={onAttach} />
      </div>

      <div className="min-w-0">
        <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">
          Version history <span className="font-semibold tabular-nums">{versions.length}</span>
        </p>
        <ol className="relative space-y-3.5 before:absolute before:bottom-2 before:left-[4px] before:top-2 before:w-0.5 before:bg-[color:var(--line-2)]">
          {versions.map((version) => {
            const current = version.id === latest.id;
            return (
              <li key={version.id} className="relative pl-6" data-testid={`document-version-${version.id}`}>
                <span
                  className={cn(
                    'absolute left-0 top-[5px] h-2.5 w-2.5 rounded-full border-2',
                    current ? 'border-brand bg-brand' : 'border-[color:var(--ink-4)] bg-surface'
                  )}
                  aria-hidden="true"
                />
                <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[12.5px] text-ink-2">
                  <span className="font-mono text-[12.5px] font-bold text-ink">v{version.version || 1}</span>
                  {version.revision_label ? <span className="text-ink-3">({version.revision_label})</span> : null}
                  <Sep />
                  <span>{formatDate(version.uploaded_at)}</span>
                  {version.uploaded_by ? <><Sep /><span>{version.uploaded_by}</span></> : null}
                  {current ? (
                    <>
                      <span className="ml-1 text-[11.5px] font-bold uppercase tracking-[0.04em] text-brand-ink">Current</span>
                      <ReviewStatusPill doc={version} />
                    </>
                  ) : (
                    <StatusPill status="Superseded" size="sm" className="ml-1" />
                  )}
                  {!current && version.review_tracked ? (
                    <span className="text-[12px] text-ink-3">was {reviewLabel(reviewStatusOf(version))}</span>
                  ) : null}
                </p>
                <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12.5px] text-ink-2">
                  <span className={cn(!String(version.remarks || '').trim() && 'text-ink-3')}>
                    {String(version.remarks || '').trim() || 'No remarks'}
                  </span>
                  {!hasFile(version) ? (
                    <>
                      <Sep />
                      <span className="font-semibold text-status-amber-ink">No file</span>
                      {canEdit ? (
                        <button type="button" className={linkClass} onClick={() => onAttach(version)} data-testid={`version-attach-${version.id}`}>
                          Attach
                        </button>
                      ) : null}
                    </>
                  ) : null}
                  {canCompareWith(version) ? (
                    <>
                      <Sep />
                      <button type="button" className={linkClass} onClick={() => onCompare(version, latest)} data-testid={`version-compare-${version.id}`}>
                        <GitCompare className="h-3.5 w-3.5" aria-hidden="true" />
                        Compare with v{latest.version || 1}
                      </button>
                    </>
                  ) : null}
                  {hasFile(version) && !current ? (
                    <>
                      <Sep />
                      <button type="button" className={linkClass} onClick={() => (isPreviewable(version) ? onOpen(version) : onDownload(version))}>
                        {isPreviewable(version) ? 'Open' : 'Download'}
                      </button>
                    </>
                  ) : null}
                </p>
              </li>
            );
          })}
        </ol>
      </div>

      <div className="flex min-w-0 flex-col gap-2 sm:col-span-2 lg:col-span-1 xl:order-last">
        {hasFile(latest) ? (
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-1">
            {isPreviewable(latest) ? (
              <Button type="button" size="sm" variant="outline" className="w-full" onClick={() => onOpen(latest)}>
                <Eye aria-hidden="true" />
                Open
              </Button>
            ) : null}
            <Button type="button" size="sm" variant="outline" className="w-full" onClick={() => onDownload(latest)}>
              <Download aria-hidden="true" />
              Download
            </Button>
          </div>
        ) : null}
        {canEdit ? (
          // Every drag event stops here (enter and leave too): the page's "Drop to upload" overlay steps
          // aside while the file is over this zone, and the page never handles the same drop.
          <div
            onDragEnter={(event) => { event.preventDefault(); event.stopPropagation(); setDragging(true); }}
            onDragOver={(event) => { event.preventDefault(); event.stopPropagation(); setDragging(true); }}
            onDragLeave={(event) => { event.stopPropagation(); setDragging(false); }}
            onDrop={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setDragging(false);
              takeFile(event.dataTransfer?.files?.[0]);
            }}
            className={cn(
              'flex flex-col items-center gap-1 rounded-[10px] border-[1.5px] border-dashed bg-surface px-3 py-3 text-center text-[12px] text-ink-3 transition-colors',
              dragging ? 'border-[color:var(--brand-ink)] bg-brand-soft' : 'border-line-2'
            )}
            data-testid={`document-version-drop-${doc.id}`}
          >
            <FileUp className="h-4 w-4 text-ink-3" aria-hidden="true" />
            <span className="font-semibold text-ink-2">
              Drop a file to save as <span className="font-mono">v{nextVersion}</span>
            </span>
            <span className="text-[11.5px]">
              v{latest.version || 1} stays as an older version ·{' '}
              <button type="button" className={linkClass} onClick={() => inputRef.current?.click()}>browse</button>
            </span>
            <input
              ref={inputRef}
              type="file"
              accept={DOCUMENT_ACCEPT}
              className="sr-only"
              tabIndex={-1}
              onChange={(event) => { takeFile(event.target.files?.[0]); event.target.value = ''; }}
            />
          </div>
        ) : null}
      </div>

      <ReviewTrail doc={latest} canEdit={canEdit} onReviewSaved={onReviewSaved} />
    </div>
  );
}

function VersionPane({ version, url, loading, error, onDownload }) {
  const ext = fileExtension(version).toLowerCase();
  const image = ['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(ext);
  return (
    <section className="flex min-h-0 min-w-0 flex-col rounded-xl border border-line bg-surface-2">
      <header className="flex items-center gap-2 border-b border-line px-3 py-2">
        <span className="font-mono text-[13px] font-bold text-ink">v{version.version || 1}</span>
        <span className="min-w-0 flex-1 truncate text-[12px] text-ink-3">
          {formatDate(version.uploaded_at)}{version.uploaded_by ? ` · ${version.uploaded_by}` : ''}{version.remarks ? ` · ${version.remarks}` : ''}
        </span>
        <Button type="button" size="xs" variant="ghost" className="text-ink-3" onClick={() => onDownload(version)} aria-label={`Download v${version.version || 1}`}>
          <Download aria-hidden="true" />
        </Button>
      </header>
      <div className="relative min-h-0 flex-1 bg-surface">
        {loading ? (
          <p className="flex h-full items-center justify-center gap-2 text-[12.5px] text-ink-3">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading v{version.version || 1}…
          </p>
        ) : error ? (
          <p className="p-4 text-[12.5px] text-status-red-ink">{error}</p>
        ) : url && image ? (
          <img src={url} alt={`Version ${version.version || 1} of ${version.title}`} className="h-full w-full object-contain" />
        ) : url ? (
          <iframe src={url} title={`Version ${version.version || 1} of ${version.title}`} className="h-full w-full border-0" />
        ) : null}
      </div>
    </section>
  );
}

function useFileUrl(version, open) {
  const [state, setState] = useState({ url: '', loading: false, error: '' });
  useEffect(() => {
    if (!open || !version || !hasFile(version)) return undefined;
    let cancelled = false;
    let objectUrl = '';
    setState({ url: '', loading: true, error: '' });
    const path = String(version.file_url).startsWith('/api/') ? String(version.file_url).slice(4) : version.file_url;
    api.get(path, { responseType: 'blob' })
      .then((response) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(response.data);
        setState({ url: objectUrl, loading: false, error: '' });
      })
      .catch((err) => { if (!cancelled) setState({ url: '', loading: false, error: getErrorMessage(err, 'Could not load this version') }); });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [version, open]);
  return state;
}

/** "Compare with v2": both versions side by side (PDFs and images open in the browser's own viewer). */
export function CompareVersionsDialog({ open, onOpenChange, older, newer, onDownload }) {
  const left = useFileUrl(older, open);
  const right = useFileUrl(newer, open);
  if (!older || !newer) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[min(860px,92vh)] w-[min(1280px,96vw)] max-w-none flex-col gap-3 rounded-card border-line bg-surface p-4 text-ink shadow-overlay sm:p-5" data-testid="compare-versions-dialog">
        <div className="pr-8">
          <DialogTitle className="text-[16px] font-bold text-ink">
            Compare <span className="font-mono">v{older.version || 1}</span> and <span className="font-mono">v{newer.version || 1}</span>
          </DialogTitle>
          <DialogDescription className="mt-0.5 text-[12.5px] text-ink-3">{newer.title}{newer.project_name ? ` · ${newer.project_name}` : ''}</DialogDescription>
        </div>
        <div className="grid min-h-0 flex-1 gap-3 md:grid-cols-2">
          <VersionPane version={older} {...left} onDownload={onDownload} />
          <VersionPane version={newer} {...right} onDownload={onDownload} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
